import React, { useEffect, useState, useCallback } from 'react';
import { repurposeAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

const SOURCE_PLATFORMS = [
  { value: 'YOUTUBE',   label: 'YouTube Video',  icon: '▶'  },
  { value: 'INSTAGRAM', label: 'Instagram Post',  icon: '📸' },
  { value: 'BLOG',      label: 'Blog Post',       icon: '📝' },
  { value: 'PODCAST',   label: 'Podcast Episode', icon: '🎙️' },
  { value: 'LINKEDIN',  label: 'LinkedIn Post',   icon: 'in' },
  { value: 'TWITTER',   label: 'Twitter Thread',  icon: '𝕏'  },
];

const TONES = [
  { value: 'CASUAL',       label: '😊 Casual',       desc: 'Friendly, conversational' },
  { value: 'PROFESSIONAL', label: '💼 Professional',  desc: 'Formal, authoritative' },
  { value: 'EDUCATIONAL',  label: '🎓 Educational',   desc: 'Informative, detailed' },
  { value: 'HUMOROUS',     label: '😂 Humorous',      desc: 'Fun, relatable, witty' },
];

const OUTPUT_TABS = [
  { key: 'ytShortScript',    label: '▶ YT Short',     icon: '▶',  color: '#ff0000', charNote: '45–60 sec'    },
  { key: 'igCaption',        label: '📸 IG Caption',  icon: '📸', color: '#c13584', charNote: 'Max 2,200'    },
  { key: 'igCarousel',       label: '🎠 Carousel',    icon: '🎠', color: '#e91e8c', charNote: '7–10 slides'  },
  { key: 'twitterThread',    label: '𝕏 Thread',       icon: '𝕏',  color: '#141414', charNote: '280/tweet'    },
  { key: 'linkedinPost',     label: 'in LinkedIn',    icon: 'in', color: '#0077b5', charNote: 'Max 3,000'    },
  { key: 'facebookPost',     label: '👍 Facebook',    icon: '👍', color: '#1877f2', charNote: 'No limit'     },
  { key: 'blogOutline',      label: '📝 Blog',        icon: '📝', color: '#ff6f00', charNote: '1,200–2,500w' },
  { key: 'emailNewsletter',  label: '📧 Email',       icon: '📧', color: '#1a7a4a', charNote: '200–400w'     },
];

const emptyForm = {
  sourceTitle: '', sourcePlatform: 'YOUTUBE', sourceDescription: '',
  keyPoints: '', targetAudience: '', tone: 'CASUAL', niche: '', cta: '',
};

export default function RepurposePage() {
  const isMobile = useIsMobile();
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(emptyForm);
  const [generating, setGenerating] = useState(false);
  const [result, setResult]       = useState(null);
  const [activeTab, setActiveTab] = useState('ytShortScript');
  const [copied, setCopied]       = useState(false);
  const [points, setPoints]       = useState(['', '', '']);
  const [step, setStep]           = useState(1); // 1=form, 2=result

  const load = useCallback(async () => {
    try { const r = await repurposeAPI.getAll(); setJobs(r.data); }
    catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (updates) => setForm(p => ({ ...p, ...updates }));

  const addPoint = () => setPoints(p => [...p, '']);
  const updatePoint = (i, val) => setPoints(p => { const n = [...p]; n[i] = val; return n; });
  const removePoint = (i) => setPoints(p => p.filter((_, idx) => idx !== i));

  const handleGenerate = async () => {
    if (!form.sourceTitle.trim()) { alert('Please enter content title'); return; }
    setGenerating(true);
    try {
      const payload = {
        ...form,
        keyPoints: points.filter(p => p.trim()).join('|'),
      };
      const res = await repurposeAPI.generate(payload);
      setResult(res.data);
      setActiveTab('ytShortScript');
      setStep(2);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed to generate'); }
    setGenerating(false);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this repurpose job?')) return;
    await repurposeAPI.delete(id);
    load();
  };

  const loadJob = (job) => {
    setResult(job);
    setActiveTab('ytShortScript');
    setStep(2);
  };

  const activeOutput = result ? result[activeTab] : '';
  const activeTabMeta = OUTPUT_TABS.find(t => t.key === activeTab);

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading…</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>Content Repurposing 🔄</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>
            One piece of content → 8 platform-ready versions instantly
          </p>
        </div>
        {step === 2 && (
          <button onClick={() => { setStep(1); setResult(null); setForm(emptyForm); setPoints(['','','']); }} style={outlineBtn}>
            ← New Repurpose
          </button>
        )}
      </div>

      {step === 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>

          {/* ── Input Form ─────────────────────────────────────── */}
          <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontWeight: 800, color: '#1a1a2e', marginBottom: '1rem', fontSize: '1rem' }}>📥 Your Content</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <label style={labelStyle}>Content Title / Topic *
                <input value={form.sourceTitle} onChange={e => f({ sourceTitle: e.target.value })} style={input}
                  placeholder="e.g. Top 5 Budget Phones Under ₹15,000 in 2025" />
              </label>

              <label style={labelStyle}>Original Platform
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginTop: '0.3rem' }}>
                  {SOURCE_PLATFORMS.map(p => (
                    <button key={p.value} onClick={() => f({ sourcePlatform: p.value })} style={{
                      padding: '0.5rem', border: '1.5px solid', borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
                      borderColor: form.sourcePlatform === p.value ? '#1a7a4a' : '#e0e0e0',
                      background: form.sourcePlatform === p.value ? '#f0f9f4' : 'white',
                      color: form.sourcePlatform === p.value ? '#1a7a4a' : '#555',
                    }}>{p.icon} {p.label}</button>
                  ))}
                </div>
              </label>

              <label style={labelStyle}>Brief Description (optional)
                <textarea value={form.sourceDescription} onChange={e => f({ sourceDescription: e.target.value })}
                  style={{ ...input, height: 65, resize: 'vertical' }}
                  placeholder="1–2 lines about what this content covers" />
              </label>

              <div style={labelStyle}>
                Key Points (add up to 7)
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.3rem' }}>
                  {points.map((pt, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <span style={{ color: '#1a7a4a', fontWeight: 800, fontSize: '0.8rem', width: 18, flexShrink: 0 }}>{i + 1}.</span>
                      <input value={pt} onChange={e => updatePoint(i, e.target.value)}
                        style={{ ...input, flex: 1 }} placeholder={`Key point ${i + 1}`} />
                      {points.length > 1 && (
                        <button onClick={() => removePoint(i)} style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem' }}>×</button>
                      )}
                    </div>
                  ))}
                  {points.length < 7 && (
                    <button onClick={addPoint} style={{ alignSelf: 'flex-start', background: 'none', border: '1px dashed #1a7a4a', borderRadius: 6, color: '#1a7a4a', fontSize: '0.78rem', fontWeight: 700, padding: '0.3rem 0.75rem', cursor: 'pointer' }}>
                      + Add Point
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <label style={labelStyle}>Niche / Topic
                  <input value={form.niche} onChange={e => f({ niche: e.target.value })} style={input} placeholder="Tech, Fitness, Finance…" />
                </label>
                <label style={labelStyle}>Target Audience
                  <input value={form.targetAudience} onChange={e => f({ targetAudience: e.target.value })} style={input} placeholder="e.g. Young professionals" />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Call to Action
                  <input value={form.cta} onChange={e => f({ cta: e.target.value })} style={input} placeholder="e.g. Drop your questions below! / Link in bio" />
                </label>
              </div>

              <div style={labelStyle}>Tone
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginTop: '0.3rem' }}>
                  {TONES.map(t => (
                    <button key={t.value} onClick={() => f({ tone: t.value })} style={{
                      padding: '0.55rem', border: '1.5px solid', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                      borderColor: form.tone === t.value ? '#1a7a4a' : '#e0e0e0',
                      background: form.tone === t.value ? '#f0f9f4' : 'white',
                    }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: form.tone === t.value ? '#1a7a4a' : '#333' }}>{t.label}</div>
                      <div style={{ fontSize: '0.7rem', color: '#888' }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={handleGenerate} disabled={generating} style={{ ...primaryBtn, width: '100%', padding: '0.75rem', fontSize: '1rem', marginTop: '0.5rem' }}>
                {generating ? '⚙️ Generating 8 versions…' : '🔄 Generate All Versions'}
              </button>
            </div>
          </div>

          {/* ── Previous Jobs ───────────────────────────────────── */}
          <div>
            <div style={{ fontWeight: 800, color: '#1a1a2e', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
              📚 Previous Repurpose Jobs
            </div>
            {jobs.length === 0 ? (
              <div style={{ background: 'white', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#aaa', fontSize: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                No jobs yet. Generate your first one →
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: 520, overflowY: 'auto' }}>
                {jobs.map(j => (
                  <div key={j.id} style={{ background: 'white', borderRadius: 12, padding: '0.9rem 1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.sourceTitle}</div>
                      <div style={{ fontSize: '0.72rem', color: '#888' }}>
                        {SOURCE_PLATFORMS.find(p => p.value === j.sourcePlatform)?.icon} {j.sourcePlatform} · {j.createdAt?.slice(0, 10)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <button onClick={() => loadJob(j)} style={{ ...iconBtn, color: '#1a7a4a', fontSize: '0.75rem', padding: '0.4rem 0.65rem' }}>View</button>
                      <button onClick={() => handleDelete(j.id)} style={{ ...iconBtn, color: '#e53e3e' }}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && result && (
        <div>
          {/* Result header */}
          <div style={{ background: '#1a1a2e', borderRadius: 14, padding: '1rem 1.5rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <div style={{ fontWeight: 800, color: 'white', fontSize: '1rem' }}>{result.sourceTitle}</div>
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.2rem' }}>
                8 versions generated · {result.tone} tone · {result.niche || 'General'}
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#4dba7a', fontWeight: 700 }}>✅ All 8 versions ready</div>
          </div>

          {/* Platform tabs */}
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {OUTPUT_TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                padding: '0.45rem 0.85rem', border: '2px solid', borderRadius: 20, cursor: 'pointer',
                fontSize: '0.78rem', fontWeight: 700, transition: 'all 0.15s',
                background: activeTab === t.key ? t.color : 'white',
                color: activeTab === t.key ? 'white' : t.color,
                borderColor: t.color,
              }}>{t.label}</button>
            ))}
          </div>

          {/* Output panel */}
          <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <span style={{ fontWeight: 800, color: activeTabMeta?.color || '#1a1a2e', fontSize: '0.95rem' }}>{activeTabMeta?.label}</span>
                <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', color: '#aaa', background: '#f5f5f5', padding: '0.2rem 0.5rem', borderRadius: 10 }}>{activeTabMeta?.charNote}</span>
              </div>
              <button onClick={() => handleCopy(activeOutput)} style={{ ...outlineBtn, fontSize: '0.78rem', padding: '0.4rem 0.85rem' }}>
                {copied ? '✅ Copied!' : '📋 Copy'}
              </button>
            </div>
            <pre style={{
              background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 12,
              padding: '1.25rem', fontFamily: 'inherit', fontSize: '0.82rem', lineHeight: 1.65,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 460, overflowY: 'auto',
              color: '#333', margin: 0,
            }}>
              {activeOutput || '(No content for this format)'}
            </pre>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {OUTPUT_TABS.filter(t => t.key !== activeTab).map(t => (
                <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                  padding: '0.3rem 0.65rem', border: '1px solid #e0e0e0', borderRadius: 15,
                  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', background: 'white', color: '#666',
                }}>
                  {t.icon} {t.label.replace(/^[▶📸🎠𝕏in👍📝📧]\s/, '')}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginTop: '1rem', background: '#fff8e1', borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#7a5c00' }}>
            💡 <b>Tip:</b> Each platform version is a starting template — personalise it with your voice before posting. The more specific your key points, the better the output.
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const primaryBtn = { background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const outlineBtn = { background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const iconBtn    = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.55rem', cursor: 'pointer', fontSize: '0.9rem' };
const input      = { padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'white' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
